import { ActorSheet } from "@thilke/ui/components/actor-sheet";
import { Button } from "@thilke/ui/components/button";
import { ChatCard } from "@thilke/ui/components/chat-card";
import { CommandPalette } from "@thilke/ui/components/command-palette";
import { CompendiumView } from "@thilke/ui/components/compendium-view";
import { ItemSheet } from "@thilke/ui/components/item-sheet";
import { JournalKbSheet } from "@thilke/ui/components/journal-kb-sheet";
import { PartyTracker } from "@thilke/ui/components/party-tracker";
import { RollCard } from "@thilke/ui/components/roll-card";
import {
  AttackGrid,
  InitiativePips,
  ProvisionGrid,
  StatField,
} from "@thilke/ui/components/tabletop-trackers";

export function ActorSheetDemo() {
  return (
    <ActorSheet
      name="Everest"
      actorType="Mech"
      system="Lancer"
      resources={[
        { label: "hp", value: 12, max: 16 },
        {
          label: "structure",
          value: 2,
          max: 4,
          display: "pips",
          tone: "destructive",
        },
        { label: "heat", value: 3, max: 8, display: "pips", tone: "warning" },
      ]}
      stats={[
        { label: "hull", value: "+2" },
        { label: "agi", value: "+1" },
        { label: "sys", value: "+0" },
        { label: "eng", value: "+3", tone: "accent" },
      ]}
      actions={<Button variant="outline">repair</Button>}
      className="w-full max-w-md"
    >
      <div className="rounded-md border bg-card p-3 text-muted-foreground">
        System-specific tabs and roll controls go here.
      </div>
    </ActorSheet>
  );
}
export function ItemSheetDemo() {
  return (
    <ItemSheet
      name="Thermal Rifle"
      itemType="Weapon"
      rarity="licensed"
      properties={[
        { label: "range", value: "10" },
        { label: "damage", value: "1d6 heat" },
      ]}
      footer="Drop item-specific editors below the description."
      className="w-full max-w-sm"
    >
      Accurate energy weapon with editable metadata blocks.
    </ItemSheet>
  );
}
export function ChatCardDemo() {
  return (
    <ChatCard
      title="Skirmish"
      subtitle="Thermal Rifle vs Assault"
      speaker="Everest"
      tone="success"
      meta="18"
      actions={<Button variant="ghost">apply</Button>}
      className="w-full max-w-sm"
    >
      Hit. Deal <strong>6 heat</strong> and mark the target impaired.
    </ChatCard>
  );
}
export function CompendiumViewDemo() {
  return (
    <CompendiumView
      title="Rules"
      entries={[
        {
          id: "1",
          title: "Overwatch",
          type: "rule",
          badge: "core",
          description:
            "Trigger a reaction when a hostile character starts movement.",
        },
        {
          id: "2",
          title: "Thermal Rifle",
          type: "item",
          badge: "weapon",
          description: "Energy weapon entry with range and damage metadata.",
        },
      ]}
      className="w-full max-w-md"
    />
  );
}
export function PartyTrackerDemo() {
  return (
    <PartyTracker
      members={[
        {
          id: "a",
          name: "Everest",
          role: "mech",
          hp: 12,
          maxHp: 16,
          status: "active",
          initiative: 14,
        },
        {
          id: "b",
          name: "Goblin",
          role: "support",
          hp: 7,
          maxHp: 10,
          status: "ready",
          initiative: 9,
        },
        {
          id: "c",
          name: "NPC Assault",
          role: "hostile",
          hp: 0,
          maxHp: 8,
          status: "down",
        },
      ]}
      className="w-full max-w-sm"
    />
  );
}
export function CommandPaletteDemo() {
  return (
    <CommandPalette
      actions={[
        { id: "actor", label: "Open Everest", group: "Actors", shortcut: "A" },
        { id: "item", label: "Create weapon", group: "Items", shortcut: "I" },
        {
          id: "rule",
          label: "Search rules",
          group: "Rules",
          detail: "compendium",
        },
      ]}
      className="w-full max-w-md"
    />
  );
}
export function JournalKbSheetDemo() {
  return (
    <JournalKbSheet
      title="Mission Brief"
      subtitle="Pinned operational notes for the current scene."
      tags={["journal", "kb"]}
      references={[
        { id: "r1", kind: "actor", label: "Everest" },
        { id: "r2", kind: "rule", label: "Overwatch" },
      ]}
      className="w-full max-w-md"
    >
      <p>Secure the relay and keep linked references one click away.</p>
    </JournalKbSheet>
  );
}

export function RollCardDemo() {
  return (
    <RollCard
      title="Attack Roll"
      subtitle="Thermal Rifle"
      speaker="Everest"
      formula="1d20+6"
      total="24"
      outcome="success"
      meta="Crit!"
      className="w-full max-w-sm"
    >
      <div className="flex flex-wrap gap-2">
        {[
          { label: "d20", value: 18, tone: "accent" },
          { label: "damage", value: 12 },
          { label: "heat", value: 6, tone: "warning" },
        ].map((die) => (
          <div
            key={die.label}
            className="flex flex-col items-center rounded border bg-card px-2 py-1 text-xs"
          >
            <span className="text-muted-foreground">{die.label}</span>
            <span className="font-mono font-bold">{die.value}</span>
          </div>
        ))}
      </div>
    </RollCard>
  );
}

export function TabletopTrackersDemo() {
  return (
    <div className="grid w-full max-w-2xl gap-4">
      <StatField
        label="HP"
        value={{ defaultValue: 12 }}
        max={{ defaultValue: 16 }}
        tone="destructive"
      />
      <StatField
        label="Heat"
        value={{ defaultValue: 3 }}
        max={{ defaultValue: 8 }}
        tone="warning"
      />
      <InitiativePips value={6} max={12} />
      <AttackGrid
        rows={[
          {
            id: "1",
            name: "Thermal Rifle",
            values: ["10", "1d6", "2"],
          },
          {
            id: "2",
            name: "Assault Blade",
            values: ["1", "2d6+2", "—"],
          },
        ]}
      />
      <ProvisionGrid
        items={[
          { id: "p1", label: "Repairs", checked: false },
          { id: "p2", label: "Core Power", checked: true },
          { id: "p3", label: "Overcharge", checked: false },
        ]}
      />
    </div>
  );
}
